import { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE } from './config';

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo-mark.svg`,
    image: DEFAULT_OG_IMAGE,
    description: DEFAULT_DESCRIPTION,
    email: 'contact@bienlivrer.ch',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rue des Oeillets 25',
      addressLocality: 'Biel/Bienne',
      postalCode: '2502',
      addressCountry: 'CH',
    },
    areaServed: [
      { '@type': 'City', name: 'Biel/Bienne' },
      { '@type': 'Country', name: 'Switzerland' },
    ],
    sameAs: [] as string[],
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    inLanguage: ['fr-CH', 'de-CH', 'en-CH', 'it-CH'],
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path.startsWith('/') ? item.path : `/${item.path}`}`,
    })),
  };
}

export function faqPageSchema(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
}

export type LocalBusinessInput = {
  name: string;
  description?: string;
  slug: string;
  image?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  ratingAvg?: number;
  ratingCount?: number;
};

export function localBusinessSchema(shop: LocalBusinessInput) {
  const url = `${SITE_URL}/shop/${shop.slug}`;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: shop.name,
    url,
    description: shop.description,
    image: shop.image,
    telephone: shop.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: shop.address,
      addressLocality: shop.city,
      postalCode: shop.zipCode,
      addressCountry: 'CH',
    },
  };

  if (shop.latitude != null && shop.longitude != null) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: shop.latitude,
      longitude: shop.longitude,
    };
  }

  if (shop.ratingAvg != null && shop.ratingCount != null && shop.ratingCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: shop.ratingAvg,
      reviewCount: shop.ratingCount,
      bestRating: 5,
      worstRating: 1,
    };
  }

  schema.areaServed = { '@type': 'City', name: shop.city?.trim() || 'Biel/Bienne' };
  schema.inLanguage = ['fr-CH', 'de-CH', 'en-CH', 'it-CH'];

  return schema;
}

const PRODUCT_JSONLD_MAX = 24;

export type ProductListItemInput = {
  name: string;
  description?: string;
  image?: string;
  price: number;
  available: boolean;
};

/** Catalogue vitrine — ItemList de Product + Offer CHF (plafond 24). */
export function productItemListSchema(input: {
  shopName: string;
  shopSlug: string;
  items: ProductListItemInput[];
}) {
  const shopUrl = `${SITE_URL}/shop/${input.shopSlug}`;
  const items = input.items.slice(0, PRODUCT_JSONLD_MAX);
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: input.shopName,
    url: shopUrl,
    numberOfItems: items.length,
    itemListElement: items.map((p, i) => {
      const product: Record<string, unknown> = {
        '@type': 'Product',
        name: p.name,
        brand: { '@type': 'Brand', name: input.shopName },
        offers: {
          '@type': 'Offer',
          url: shopUrl,
          price: Number(p.price).toFixed(2),
          priceCurrency: 'CHF',
          availability: p.available
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        },
      };
      if (p.description) product.description = p.description;
      if (p.image) product.image = p.image;
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: product,
      };
    }),
  };
}
